import {
  type CatalogWorkerRequest,
  type CatalogWorkerResponse,
  type CountryCode,
  type SearchDocument,
  type SearchOutcome,
} from '@der-maschinfuehrer/catalog/worker-types';
import { locationSnapshotSchema, type LocationSnapshot } from '@der-maschinfuehrer/contracts';

import {
  CATALOG_SEARCH_DOCUMENT_SCHEMA,
  CATALOG_WORKER_PROTOCOL,
  catalogLocationUuid,
  type CatalogAppWorkerRequest,
} from './catalog-artifact';
import { WEB_CATALOG } from './generated-catalog';

type CatalogUsage = Extract<CatalogWorkerRequest, { readonly type: 'set_usage' }>['usage'];

const LOCATION_TYPE_PROJECTION = {
  station: 'station',
  halt: 'halt',
  junction: 'junction',
  boundary: 'border_point',
  yard: 'yard',
  depot: 'depot',
  service_point: 'operational_point',
  other: 'other',
  unknown: 'other',
} as const satisfies Readonly<Record<SearchDocument['type'], LocationSnapshot['location_type']>>;

interface PendingRequest {
  readonly reject: (reason: Error) => void;
  readonly resolve: (response: CatalogWorkerResponse) => void;
  readonly timeout: ReturnType<typeof setTimeout>;
}

export class CatalogClient {
  readonly #generation = 1;
  #initialization: Promise<void> | null = null;
  readonly #localProfileId: string;
  readonly #pending = new Map<string, PendingRequest>();
  #requestSequence = 0;
  #worker: Worker | null = null;

  public constructor(localProfileId: string) {
    this.#localProfileId = localProfileId;
  }

  public prepare(): Promise<void> {
    return this.#initialize();
  }

  public async query(query: string, country: CountryCode | null = null): Promise<SearchOutcome> {
    await this.#initialize();
    const response = await this.#send({
      protocol: CATALOG_WORKER_PROTOCOL,
      requestId: this.#requestId(),
      type: 'query',
      localProfileId: this.#localProfileId,
      generation: this.#generation,
      query,
      limit: 100,
      namespace: null,
      country,
    });
    if (
      response.type !== 'results' ||
      response.catalogVersion !== WEB_CATALOG.catalogVersion ||
      response.localProfileId !== this.#localProfileId ||
      response.generation !== this.#generation
    ) {
      throw new Error('Katalogový Worker vrátil neočekávanou odpověď.');
    }
    return response.outcome;
  }

  /**
   * The durable domain layer stores LocationSnapshot UUIDs, while the isolated
   * Worker ranks its own catalog document IDs. The Worker translates only
   * snapshots that belong to its active immutable artifact; user-defined and
   * stale locations deliberately stay outside catalog ranking.
   */
  public async setUsage(usage: CatalogUsage): Promise<void> {
    await this.#initialize();
    const response = await this.#send({
      protocol: CATALOG_WORKER_PROTOCOL,
      requestId: this.#requestId(),
      type: 'set_usage',
      localProfileId: this.#localProfileId,
      generation: this.#generation,
      usage,
    });
    if (
      response.type !== 'ack' ||
      response.operation !== 'set_usage' ||
      response.localProfileId !== this.#localProfileId ||
      response.generation !== this.#generation
    ) {
      throw new Error('Katalogový Worker nepotvrdil pořadí nedávných a oblíbených míst.');
    }
  }

  public dispose(): void {
    const worker = this.#worker;
    if (worker !== null) {
      try {
        worker.postMessage({
          protocol: CATALOG_WORKER_PROTOCOL,
          requestId: this.#requestId(),
          type: 'dispose',
          localProfileId: this.#localProfileId,
          generation: this.#generation,
        } satisfies CatalogAppWorkerRequest);
      } catch {
        // Termination below remains the definitive local cleanup boundary.
      }
    }
    this.#worker = null;
    this.#initialization = null;
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('Katalogový Worker byl ukončen.'));
    }
    this.#pending.clear();
    worker?.terminate();
  }

  async #initialize(): Promise<void> {
    if (this.#initialization === null) {
      const attempt = this.#initializeOnce();
      this.#initialization = attempt;
      void attempt.catch(() => {
        if (this.#initialization === attempt) {
          this.#worker?.terminate();
          this.#worker = null;
          this.#initialization = null;
        }
      });
    }
    return this.#initialization;
  }

  async #initializeOnce(): Promise<void> {
    const worker = new Worker(new URL('../workers/catalog-worker.ts', import.meta.url), {
      name: 'dmf-catalog-search',
      type: 'module',
    });
    this.#worker = worker;
    worker.addEventListener('message', (event: MessageEvent<unknown>) => {
      this.#handleResponse(event.data);
    });
    worker.addEventListener('error', () => {
      this.#handleWorkerFailure(
        new Error('Katalogový Worker selhal. Ruční zadání zůstává dostupné.'),
      );
    });
    worker.addEventListener('messageerror', () => {
      this.#handleWorkerFailure(new Error('Katalogový Worker nepřijal odpověď.'));
    });

    const ready = await this.#send(
      {
        protocol: CATALOG_WORKER_PROTOCOL,
        requestId: this.#requestId(),
        type: 'initialize_artifact',
        localProfileId: this.#localProfileId,
        generation: this.#generation,
        artifactUrl: WEB_CATALOG.artifactUrl,
        catalogVersion: WEB_CATALOG.catalogVersion,
        formatVersion: WEB_CATALOG.formatVersion,
        documentCount: WEB_CATALOG.documentCount,
        schemaVersion: CATALOG_SEARCH_DOCUMENT_SCHEMA,
      },
      30_000,
    );
    if (
      ready.type !== 'ready' ||
      ready.catalogVersion !== WEB_CATALOG.catalogVersion ||
      ready.documentCount !== WEB_CATALOG.documentCount ||
      ready.localProfileId !== this.#localProfileId ||
      ready.generation !== this.#generation
    ) {
      throw new Error('Katalogový Worker nepotvrdil přesnou verzi katalogu.');
    }
  }

  #send(request: CatalogAppWorkerRequest, timeoutMs = 8_000): Promise<CatalogWorkerResponse> {
    const worker = this.#worker;
    if (worker === null) {
      return Promise.reject(new Error('Katalogový Worker není spuštěný.'));
    }
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.#pending.delete(request.requestId);
        reject(new Error('Katalogový Worker neodpověděl včas.'));
      }, timeoutMs);
      this.#pending.set(request.requestId, { reject, resolve, timeout });
      worker.postMessage(request);
    });
  }

  #handleResponse(value: unknown): void {
    if (!isCatalogWorkerResponse(value)) {
      this.#failPending(new Error('Katalogový Worker vrátil neplatnou odpověď.'));
      return;
    }
    const pending = this.#pending.get(value.requestId);
    if (pending === undefined) return;
    clearTimeout(pending.timeout);
    this.#pending.delete(value.requestId);
    if (value.type === 'error') {
      pending.reject(new Error(value.message));
    } else {
      pending.resolve(value);
    }
  }

  #failPending(error: Error): void {
    for (const pending of this.#pending.values()) {
      clearTimeout(pending.timeout);
      pending.reject(error);
    }
    this.#pending.clear();
  }

  #handleWorkerFailure(error: Error): void {
    this.#failPending(error);
    this.#worker?.terminate();
    this.#worker = null;
    this.#initialization = null;
  }

  #requestId(): string {
    this.#requestSequence += 1;
    return `catalog-${String(this.#requestSequence)}-${crypto.randomUUID()}`;
  }
}

/**
 * Stable app-facing snapshot projection for the current OSM search artifact.
 * Its location UUID is byte-for-byte compatible with the catalog core's
 * dmf.catalog.uuid.v1 `location` derivation.
 */
export function catalogDocumentToLocationSnapshot(document: SearchDocument): LocationSnapshot {
  const catalogIdentifiers = [...document.identifiers]
    .sort((left, right) =>
      compareCodeUnits(
        `${left.namespace}\u0000${left.rawValue}`,
        `${right.namespace}\u0000${right.rawValue}`,
      ),
    )
    .map((identifier) => ({
      namespace: identifier.namespace,
      namespace_other: null,
      value: identifier.rawValue,
    }));

  return locationSnapshotSchema.parse({
    location_id: catalogLocationUuid(document.id),
    display_name: document.officialName,
    location_type: LOCATION_TYPE_PROJECTION[document.type],
    country_code: document.country,
    key_identifiers: [
      ...catalogIdentifiers,
      {
        namespace: 'OTHER',
        namespace_other: 'OSM',
        value: document.id,
      },
    ],
  });
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function isCatalogWorkerResponse(value: unknown): value is CatalogWorkerResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'protocol' in value &&
    value.protocol === CATALOG_WORKER_PROTOCOL &&
    'requestId' in value &&
    typeof value.requestId === 'string' &&
    'type' in value &&
    typeof value.type === 'string'
  );
}
